'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { CheckCircle, Zap, Users, Trophy } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const plans = [
  {
    id: 'plan-family',
    name: 'Family',
    icon: Users,
    price: 10,
    period: 'month',
    tagline: 'Perfect for families of up to 4 players',
    color: 'border-teal-200',
    badge: null,
    features: [
      'Up to 4 verified players',
      'All 40+ platform integrations',
      'Public message boards access',
      'Parent dashboard & alerts',
      'Private game rooms (3 max)',
      'Basic tournament access',
      'Email support',
    ],
    cta: 'Start Family Plan',
    ctaStyle: 'bg-teal-700 hover:bg-teal-800 text-white',
  },
  {
    id: 'plan-premium',
    name: 'Premium',
    icon: Zap,
    price: 20,
    period: 'month',
    tagline: 'Up to 10 players with advanced features',
    color: 'border-amber-400',
    badge: 'Most Popular',
    features: [
      'Up to 10 verified players',
      'All 40+ platform integrations',
      'Public & private boards',
      'Advanced parent controls',
      'Unlimited private game rooms',
      'Tournament hosting (5/month)',
      'Priority support',
      'Weekly activity reports',
      'Custom player profiles',
    ],
    cta: 'Start Premium Plan',
    ctaStyle: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  {
    id: 'plan-team',
    name: 'Team / League',
    icon: Trophy,
    price: null,
    period: null,
    tagline: 'For esports leagues, schools, and clubs',
    color: 'border-purple-200',
    badge: 'Custom Pricing',
    features: [
      'Unlimited verified players',
      'Dedicated league dashboard',
      'Full tournament season hosting',
      'Bracket automation & seeding',
      'Verified spectator access',
      'Coach & admin accounts',
      'API access for integrations',
      'Dedicated account manager',
      'SLA-backed uptime guarantee',
    ],
    cta: 'Contact Sales',
    ctaStyle: 'bg-purple-700 hover:bg-purple-800 text-white',
  },
];

export default function PricingSection() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="py-20 bg-slate-50" aria-labelledby="pricing-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Pricing</span>
          <h2 id="pricing-heading" className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Simple, honest pricing
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto mb-6">
            One free month for every new family. No hidden fees. Cancel anytime.
          </p>

          {/* Toggle */}
          <div className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full px-4 py-2" role="group" aria-label="Billing period selection">
            <span className={`text-sm font-medium ${!annual ? 'text-slate-900' : 'text-slate-500'}`} id="billing-monthly">Monthly</span>
            <button
              onClick={() => setAnnual(!annual)}
              className={`relative w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${annual ? 'bg-teal-600' : 'bg-slate-300'}`}
              aria-label={annual ? 'Switch to monthly billing' : 'Switch to annual billing'}
              aria-pressed={annual}
              role="switch"
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${annual ? 'translate-x-5' : 'translate-x-0'}`} aria-hidden="true" />
            </button>
            <span className={`text-sm font-medium ${annual ? 'text-slate-900' : 'text-slate-500'}`} id="billing-annual">
              Annual{' '}
              <span className="text-green-700 font-semibold">Save 20%</span>
            </span>
          </div>
        </div>

        {/* Promo banner */}
        <div className="max-w-2xl mx-auto mb-8 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-center gap-3" role="note" aria-label="Promotional offer: First month free">
          <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
            <span className="text-white text-sm">🎁</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-900">First month completely free for all new families</p>
            <p className="text-xs text-amber-800 mt-0.5">No credit card charged until your trial ends. Use code <span className="font-mono font-bold">SAFEPLAY1FREE</span> at checkout.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans?.map((plan) => {
            const Icon = plan?.icon;
            const price = plan?.price && annual ? Math.round(plan?.price * 0.8) : plan?.price;
            return (
              <article
                key={plan?.id}
                className={`relative bg-white border-2 ${plan?.color} rounded-2xl p-6 flex flex-col hover:shadow-xl transition-all duration-200 ${plan?.badge === 'Most Popular' ? 'shadow-lg scale-[1.02]' : ''}`}
                aria-label={`${plan?.name} plan${plan?.badge ? ` — ${plan?.badge}` : ''}`}
              >
                {plan?.badge && (
                  <div className={`absolute -top-3.5 left-1/2 -translate-x-1/2 text-xs font-bold px-4 py-1 rounded-full ${plan?.badge === 'Most Popular' ? 'bg-amber-500 text-white' : 'bg-purple-100 text-purple-800 border border-purple-200'}`} aria-label={plan?.badge}>
                    {plan?.badge}
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan?.id === 'plan-family' ? 'bg-teal-100 text-teal-700' : plan?.id === 'plan-premium' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`} aria-hidden="true">
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg text-slate-900">{plan?.name}</h3>
                  </div>
                </div>
                <div className="mb-2">
                  {price !== null ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-display font-bold text-slate-900 tabular-nums" aria-label={`$${price} per month`}>${price}</span>
                      <span className="text-slate-600 text-sm" aria-hidden="true">/month</span>
                    </div>
                  ) : (
                    <span className="text-2xl font-display font-bold text-slate-900">Custom pricing</span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mb-6">{plan?.tagline}</p>
                <ul className="space-y-2.5 mb-8 flex-1" aria-label={`${plan?.name} plan features`}>
                  {plan?.features?.map((feat) => (
                    <li key={`${plan?.id}-feat-${feat}`} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle size={15} className="text-teal-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      {feat}
                    </li>
                  ))}
                </ul>
                <Link
                  href="/payment-checkout"
                  className={`w-full text-center py-3 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 ${plan?.ctaStyle}`}
                  aria-label={`${plan?.cta} — ${plan?.name} plan`}
                >
                  {plan?.cta}
                </Link>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}